import { useRef } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { closeSnackbar, SnackbarProvider as NotistackProvider } from 'notistack';

import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import MuiInfoIcon from '@mui/icons-material/Info';
import MuiCloseIcon from '@mui/icons-material/Close';
import GlobalStyles from '@mui/material/GlobalStyles';
import MuiWarningIcon from '@mui/icons-material/Warning';
import MuiCheckCircleIcon from '@mui/icons-material/CheckCircle';

import MuiIcon from 'src/components/mui-icon';

import { useSettingsContext } from '../settings';
import LoadingModal from '../Modals/LoadingModal';
import { StyledIcon, StyledNotistack } from './styles';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function SnackbarProvider({ children }: Props) {
  const settings = useSettingsContext();

  const isRTL = settings.themeDirection === 'rtl';

  const notistackRef = useRef<any>(null);
  // azed loading modal
  NiceModal.register('LoadingModal', LoadingModal);

  return (
    <>
      {/*
        Push every TOP-anchored notistack container below the device safe area so
        toasts clear the notch / Dynamic Island on iOS. `env(safe-area-inset-top)`
        resolves to 0 on web and non-notched devices, so this is a no-op there.
        Scoped to the top anchors only — bottom containers are positioned with
        `bottom` and must stay untouched.
      */}
      <GlobalStyles
        styles={{
          '.notistack-snackbar-safe-top': {
            top: 'calc(env(safe-area-inset-top) + 14px) !important',
          },
        }}
      />
      <NotistackProvider
        ref={notistackRef}
        maxSnack={5}
        preventDuplicate
        autoHideDuration={3000}
        TransitionComponent={isRTL ? Collapse : undefined}
        variant="success" // Set default variant
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{
          containerAnchorOriginTopRight: 'notistack-snackbar-safe-top',
          containerAnchorOriginTopLeft: 'notistack-snackbar-safe-top',
          containerAnchorOriginTopCenter: 'notistack-snackbar-safe-top',
        }}
        iconVariant={{
          info: (
            <StyledIcon color="info">
              <MuiIcon icon={MuiInfoIcon} width={24} />
            </StyledIcon>
          ),
          success: (
            <StyledIcon color="success">
              <MuiIcon icon={MuiCheckCircleIcon} width={24} />
            </StyledIcon>
          ),
          warning: (
            <StyledIcon color="warning">
              <MuiIcon icon={MuiWarningIcon} width={24} />
            </StyledIcon>
          ),
          error: (
            <StyledIcon color="error">
              <MuiIcon icon={MuiWarningIcon} width={24} />
            </StyledIcon>
          ),
        }}
        Components={{
          default: StyledNotistack,
          info: StyledNotistack,
          success: StyledNotistack,
          warning: StyledNotistack,
          error: StyledNotistack,
        }}
        // with close as default
        action={(snackbarId) => (
          <IconButton size="small" onClick={() => closeSnackbar(snackbarId)} sx={{ p: 0.5 }}>
            <MuiIcon width={16} icon={MuiCloseIcon} />
          </IconButton>
        )}
      >
        {children}
      </NotistackProvider>
    </>
  );
}
